/**
 * Layout module — applies WidgetConfig.layout to the injected iframe.
 *
 * Trust model: the loader trusts a single origin (the script's src origin).
 * The customCss string has already been sanitized server-side by
 * CustomCssSanitizer; this module does NOT re-sanitize. We do, however,
 * defend the host page from a few host-side concerns:
 *   - Constrain the mount-selector lookup to id-or-class form (mirrors the
 *     server validator) so we don't hand an arbitrary querySelector string
 *     back to the host DOM.
 *   - Keep the preset CSS strings here in lock-step with
 *     `widget-studio.component.ts` LAYOUT_PRESET_CSS.
 *   - Apply the iframe class hook ('apptor-frame') so customer overrides land.
 */

export type LayoutPreset = 'floating' | 'inline' | 'fullscreen';

export interface LayoutConfig {
  preset: LayoutPreset;
  mount: string;
  customCss: string;
}

const STYLE_TAG_ID = 'apptor-layout-css';
const FRAME_CLASS = 'apptor-frame';

/** Strict mirror of WidgetConfigValidator.MOUNT_SELECTOR — single id or class
 *  token, 1–64 chars, alnum + - + _, must start with [#.] then a letter. */
const MOUNT_SELECTOR_RE = /^[#.][A-Za-z][A-Za-z0-9_-]{0,63}$/;

/** Default CSS each preset compiles to. Must match the studio's
 *  LAYOUT_PRESET_CSS exactly so the live preview shows what production will. */
const PRESET_CSS: Record<LayoutPreset, string> = {
  floating: `.apptor-frame {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: min(380px, 92vw);
  height: min(560px, 80vh);
  border: 0;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.35);
  background: transparent;
  z-index: 2147483646;
}
@media (max-width: 640px) {
  .apptor-frame {
    width: 100vw;
    height: 100vh;
    bottom: 0;
    right: 0;
    border-radius: 0;
  }
}`,
  inline: `.apptor-frame {
  position: relative;
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
}`,
  fullscreen: `.apptor-frame {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  border: 0;
  border-radius: 0;
  background: transparent;
  z-index: 2147483646;
}`
};

/** Look up the mount node for `inline` preset synchronously. Returns null when:
 *   - selector is empty or malformed,
 *   - the host has no element matching it RIGHT NOW.
 *  See {@link awaitMount} for the reactive variant that waits for SPA hydration. */
export function resolveMount(selector: string | null | undefined): HTMLElement | null {
  if (!selector) return null;
  const s = selector.trim();
  if (!MOUNT_SELECTOR_RE.test(s)) return null;
  const el = document.querySelector(s);
  return el instanceof HTMLElement ? el : null;
}

/** Reactive mount resolution. Returns the element synchronously if it already
 *  exists; otherwise installs a MutationObserver on document.body that resolves
 *  as soon as the host inserts a matching element (typical for React / Vue /
 *  Angular hosts whose hydration runs AFTER the async loader script).
 *
 *  Times out after `timeoutMs` (default 30s) and resolves null so the caller
 *  can fall back to floating. Returns the disposer the caller invokes on
 *  destroy / re-boot to release the observer. */
export function awaitMount(
  selector: string | null | undefined,
  cb: (el: HTMLElement | null) => void,
  timeoutMs: number = 30_000
): () => void {
  // Fast path — synchronous match.
  const sync = resolveMount(selector);
  if (sync) {
    cb(sync);
    return () => { /* nothing to clean up */ };
  }
  // Empty / malformed selector — no point watching.
  if (!selector || !MOUNT_SELECTOR_RE.test(selector.trim())) {
    cb(null);
    return () => { /* nothing to clean up */ };
  }
  let resolved = false;
  const finish = (el: HTMLElement | null) => {
    if (resolved) return;
    resolved = true;
    observer.disconnect();
    clearTimeout(timer);
    cb(el);
  };
  const observer = new MutationObserver(() => {
    const el = resolveMount(selector);
    if (el) finish(el);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  const timer = setTimeout(() => finish(null), timeoutMs);
  return () => finish(null);
}

/** Apply layout to an injected iframe. Idempotent: replaces the existing
 *  <style id="apptor-layout-css"> on each call so update() works without
 *  leaking duplicate stylesheets. */
export function applyLayout(iframe: HTMLIFrameElement, layout: LayoutConfig): void {
  iframe.classList.add(FRAME_CLASS);
  // Clear any inline style cssText that an earlier injectIframe applied — the
  // stylesheet now drives positioning. Inline styles would beat the stylesheet
  // and trap customers in the original 380×560 BR rectangle.
  iframe.removeAttribute('style');
  iframe.style.display = 'none'; // remain hidden until open() flips display

  const css = (PRESET_CSS[layout.preset] || PRESET_CSS.floating)
    + '\n\n/* customer override */\n'
    + (layout.customCss || '');

  let tag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  tag.textContent = css;
}

/** Tear down the stylesheet — called by ApptorChat.destroy(). */
export function removeLayoutStyle(): void {
  const tag = document.getElementById(STYLE_TAG_ID);
  if (tag) tag.remove();
}

/** Show/hide that respect the stylesheet's display property. We can't use
 *  iframe.style.display because the stylesheet's `display: block` would lose
 *  to it on first paint; setting display via a class instead. */
export function showFrame(iframe: HTMLIFrameElement): void { iframe.style.display = 'block'; }
export function hideFrame(iframe: HTMLIFrameElement): void { iframe.style.display = 'none'; }
